import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Trash2, Search } from "lucide-react";
import { getAuthErrorMessage, loginInputClass } from "../LoginForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("comp_token");
export const NOTE_TYPES = ["Normál", "Dicséret", "Figyelmeztetés", "Felszólítás"];
export const NOTE_STYLE = {
  "Normál": "bg-white text-black ring-1 ring-slate-200",
  "Dicséret": "bg-emerald-100 text-black ring-2 ring-emerald-300 shadow-md shadow-emerald-500/20",
  "Figyelmeztetés": "bg-yellow-100 text-black ring-1 ring-yellow-300",
  "Felszólítás": "bg-red-100 text-black ring-1 ring-red-300",
};
export const fmtDate = (d) => (d ? d.replace(/-/g, ". ") + "." : "");

export const NoteCard = ({ n, children }) => (
  <div className={`rounded-2xl p-4 ${NOTE_STYLE[n.type] || NOTE_STYLE["Normál"]}`} data-testid={`note-card-${n.id}`} data-type={n.type}>
    <div className="mb-2 flex items-start justify-between gap-3">
      <span className="rounded-full bg-black/10 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-black" data-testid={`note-type-${n.id}`}>{n.type}</span>
      <span className="text-xs font-medium text-black">{fmtDate(n.date)}</span>
    </div>
    <p className="whitespace-pre-wrap text-sm leading-relaxed text-black">{n.description}</p>
    {children}
  </div>
);

export function FeljegyzesekTab() {
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState(null);
  const [form, setForm] = useState({ all: false, recipient_ids: [], type: "Normál", date: new Date().toISOString().slice(0, 10), description: "" });
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const P = { params: { token: token() } };

  const load = () => Promise.all([axios.get(`${API}/competition/users`, P), axios.get(`${API}/ugyfelkapu/admin/notes`, P)])
    .then(([u, n]) => { setUsers([...u.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "hu"))); setNotes(n.data); })
    .catch((e) => { toast.error(getAuthErrorMessage(e)); setNotes([]); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => users.filter((u) => !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.identifier || "").toLowerCase().includes(q.toLowerCase())), [users, q]);
  const toggle = (id) => setForm((f) => ({ ...f, recipient_ids: f.recipient_ids.includes(id) ? f.recipient_ids.filter((x) => x !== id) : [...f.recipient_ids, id] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.all && form.recipient_ids.length === 0) { toast.error("Válassz címzettet vagy a Mindenki opciót"); return; }
    if (!form.description.trim()) { toast.error("A leírás kötelező"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ugyfelkapu/admin/notes`, form, P);
      toast.success("Feljegyzés elküldve");
      setForm({ ...form, recipient_ids: [], all: false, description: "" });
      load();
    } catch (err) { toast.error(getAuthErrorMessage(err)); }
    setBusy(false);
  };
  const remove = async (n) => {
    if (!window.confirm("Törlöd ezt a feljegyzést? Minden címzettnél eltűnik.")) return;
    try { await axios.delete(`${API}/ugyfelkapu/admin/notes/${n.id}`, P); toast.success("Feljegyzés törölve"); load(); } catch (err) { toast.error(getAuthErrorMessage(err)); }
  };

  const card = "rounded-3xl bg-white p-5 sm:p-7 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100";
  return (
    <div className="mx-auto w-full max-w-4xl space-y-6" data-testid="feljegyzesek-tab">
      <form onSubmit={submit} className={`${card} space-y-4`} data-testid="note-form">
        <h2 className="text-lg font-bold text-slate-900">Új feljegyzés</h2>
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <label className="text-sm font-medium text-black">Címzettek {form.all ? "(mindenki)" : `(${form.recipient_ids.length} kiválasztva)`}</label>
            {!form.all && form.recipient_ids.length > 0 && <p className="w-full text-xs text-black" data-testid="note-selected-names">Kiválasztva: {users.filter((u) => form.recipient_ids.includes(u.id)).map((u) => u.name).join(", ")}</p>}
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700"><input type="checkbox" checked={form.all} onChange={(e) => setForm({ ...form, all: e.target.checked })} className="h-4 w-4 accent-sky-600" data-testid="note-all-checkbox" /> Mindenki</label>
          </div>
          {!form.all && (
            <div className="rounded-2xl border border-slate-200 p-3">
              <div className="relative mb-2"><Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés..." className={`${loginInputClass} h-10 pl-9`} data-testid="note-recipient-search" /></div>
              <div className="grid max-h-44 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2" data-testid="note-recipient-list">
                {filtered.map((u) => (
                  <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50">
                    <input type="checkbox" checked={form.recipient_ids.includes(u.id)} onChange={() => toggle(u.id)} className="h-4 w-4 accent-sky-600" data-testid={`note-recipient-${u.id}`} />
                    <span className="truncate text-black">{u.name}</span><span className="font-mono text-[11px] text-slate-700">{u.identifier}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Típus</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={loginInputClass} data-testid="note-type-select">{NOTE_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Dátum</label>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={loginInputClass} data-testid="note-date" /></div>
        </div>
        <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Leírás</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className={`${loginInputClass} h-auto py-3`} data-testid="note-description" /></div>
        <button type="submit" disabled={busy} className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-sky-600 font-semibold text-white hover:bg-sky-700 disabled:opacity-60" data-testid="note-send-btn"><Send size={16} /> {busy ? "Küldés..." : "Küldés"}</button>
      </form>

      <section className={card} data-testid="notes-admin-list">
        <h2 className="mb-4 text-lg font-bold text-slate-900">Elküldött feljegyzések</h2>
        {!notes ? <p className="py-6 text-center text-slate-600">Betöltés...</p> : notes.length === 0 ? <p className="py-6 text-center text-sm text-slate-600">Még nincs feljegyzés.</p> : (
          <div className="grid gap-3 sm:grid-cols-2">
            {notes.map((n) => (
              <NoteCard key={n.id} n={n}>
                <div className="mt-3 flex items-end justify-between gap-2 text-xs text-black">
                  <span data-testid={`note-recipients-${n.id}`} className="text-black">Címzettek: {n.to_all ? `mindenki (${n.recipient_ids.length})` : n.recipient_names.join(", ")}</span>
                  <button type="button" onClick={() => remove(n)} className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-red-600 px-2.5 py-1 font-semibold text-white hover:bg-red-700" data-testid={`note-delete-${n.id}`}><Trash2 size={13} /> Törlés</button>
                </div>
              </NoteCard>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default FeljegyzesekTab;
